import {
  createBrowserRouter,
  Navigate,
  Outlet,
  type RouteObject,
  RouterProvider,
  RoutesProvider,
  ScrollRestoration,
  useLocation,
} from '@faceup/router'
import { getValidBasename, languageEnumToBasename } from '@faceup/utils'
import { lazy, Suspense, useMemo } from 'react'
import { FullScreenLoader } from '../../Components/FullScreenLoader'
import { NavigationChange } from '../../Components/NavigationChange'
import { PointerEventsMember } from '../../Components/PointerEventsMember'
import { RouterFullScreenLoader } from '../../Components/RouterFullScreenLoader'
import { useRoutes } from '../../hooks/useRoutes'
import { useSavedReportsContext } from '../../hooks/useSavedReportsContext'
import { useAccessKey } from '../AccessKeyProvider'
import { AppErrorBoundary } from '../AppErrorBoundary'
import { useAppLock } from '../AppLockProvider'
import { useClient } from '../ClientProvider'
import { getInitialLanguage } from '../initialLanguage'
import { DisclosureSubmissionFormWrapper } from './components/DisclosureSubmissionFormWrapper'
import { ReportSourceWrapper, reportLayoutHandle } from './components/ReportSourceWrapper'
import { SurveyWrapper } from './components/SurveyWrapper'

const SearchInstitutionView = lazy(() =>
  import('../../Pages/SearchInstitutionView/SearchInstitutionView').then(module => ({
    default: module.SearchInstitutionView,
  }))
)
const EmailVerificationView = lazy(() =>
  import('../../Components/EmailVerificationView').then(module => ({
    default: module.EmailVerificationView,
  }))
)
const OfflineView = lazy(() =>
  import('../../Pages/OfflineView/OfflineView').then(module => ({ default: module.OfflineView }))
)
const BiometricsView = lazy(() =>
  import('../../Pages/Authentication/BiometricsView').then(module => ({
    default: module.BiometricsView,
  }))
)
const LandingPage = lazy(() =>
  import('../../Pages/Authentication/LandingPage').then(module => ({ default: module.LandingPage }))
)
const CreateReportView = lazy(() =>
  import('../../Pages/CreateReportView/CreateReportView').then(module => ({
    default: module.CreateReportView,
  }))
)
const ReportSentView = lazy(() =>
  import('../../Pages/ReportSentView/ReportSentView').then(module => ({
    default: module.ReportSentView,
  }))
)
const CheckReportView = lazy(() =>
  import('../../Pages/CheckReportView/CheckReportView').then(module => ({
    default: module.CheckReportView,
  }))
)
const CheckReportListView = lazy(() =>
  import('../../Pages/CheckReportListView/CheckReportListView').then(module => ({
    default: module.CheckReportListView,
  }))
)
const ReportView = lazy(() =>
  import('../../Pages/ReportView/ReportView').then(module => ({ default: module.ReportView }))
)
const PageView = lazy(() =>
  import('../../Pages/PageView/PageView').then(module => ({ default: module.PageView }))
)
const DefaultPageRedirect = lazy(() =>
  import('../../Pages/PageView/DefaultPageRedirect').then(module => ({
    default: module.DefaultPageRedirect,
  }))
)
const SurveyView = lazy(() =>
  import('../../Pages/SurveyView/SurveyView').then(module => ({ default: module.SurveyView }))
)
const SurveySentView = lazy(() =>
  import('../../Pages/SurveySentView').then(module => ({ default: module.SurveySentView }))
)
const DisclosureSubmissionFormView = lazy(() =>
  import('../../Pages/DisclosureSubmission/DisclosureSubmissionFormView').then(module => ({
    default: module.DisclosureSubmissionFormView,
  }))
)
const DisclosureSubmissionSentView = lazy(() =>
  import('../../Pages/DisclosureSubmission/DisclosureSubmissionSentView').then(module => ({
    default: module.DisclosureSubmissionSentView,
  }))
)

const branchFallback = <FullScreenLoader />

const surveyRoutes: RouteObject[] = [
  {
    path: ':language/q/:shortId',
    element: (
      <Suspense fallback={branchFallback}>
        <SurveyWrapper />
      </Suspense>
    ),
    children: [
      { index: true, element: <SurveyView /> },
      { path: 'sent', element: <SurveySentView /> },
    ],
  },
]

const disclosureSubmissionRoutes: RouteObject[] = [
  {
    path: ':language/d/:shortId',
    element: (
      <Suspense fallback={branchFallback}>
        <DisclosureSubmissionFormWrapper />
      </Suspense>
    ),
    children: [
      { index: true, element: <DisclosureSubmissionFormView /> },
      { path: 'sent', element: <DisclosureSubmissionSentView /> },
    ],
  },
]

/**
 * `check` route guard: app users who already have saved reports see the list
 * of their reports (CheckReportList); everyone else - web always, plus app
 * users with no saved reports - gets the CheckReport PIN form. `check/form`
 * always renders the PIN form so the list's "check another report" button has
 * a target.
 */
const CheckReportRoute = () => {
  const { client } = useClient()
  const { hasSomeSavedReports } = useSavedReportsContext()

  if (client === 'app' && hasSomeSavedReports) {
    return <CheckReportListView />
  }
  return <CheckReportView />
}

const reportRoutes: RouteObject[] = [
  {
    path: ':language/c/:shortId',
    element: (
      <Suspense fallback={branchFallback}>
        <ReportSourceWrapper />
      </Suspense>
    ),
    children: [
      { index: true, element: <Navigate to={routes => routes.page()} replace /> },
      {
        path: 'create',
        element: <CreateReportView />,
        handle: reportLayoutHandle({ variant: 'simple', topAligned: true }),
      },
      {
        path: 'sent',
        element: <ReportSentView />,
        handle: reportLayoutHandle({ variant: 'simple' }),
      },
      {
        path: 'check',
        element: <CheckReportRoute />,
        handle: reportLayoutHandle({ variant: 'simple' }),
      },
      {
        path: 'check/form',
        element: <CheckReportView />,
        handle: reportLayoutHandle({ variant: 'simple' }),
      },
      {
        path: 'report',
        element: <ReportView />,
        handle: reportLayoutHandle({ variant: 'simple' }),
      },
      { path: 'page/:id', element: <PageView /> },
      { path: 'page', element: <DefaultPageRedirect /> },
    ],
  },
  {
    path: ':language/s/:shortId',
    element: <Navigate to={routes => routes.page()} replace />,
  },
]

/**
 * Home index guard: app users with an unfinished setup go to biometrics, app
 * users with a remembered institution skip search and go straight to its page,
 * everyone else searches. On the web isSetUp() is always true and there is no
 * remembered access key, so it is just SearchInstitution.
 */
const HomeRedirect = () => {
  const { isSetUp } = useAppLock()
  const { accessKey } = useAccessKey()

  if (!isSetUp()) {
    return <Navigate to={routes => routes.settingsBiometrics()} replace />
  }
  if (accessKey?.shortLink) {
    return <Navigate to={routes => routes.page({ shortId: accessKey.shortLink })} replace />
  }
  return <SearchInstitutionView />
}

const homeRoutes: RouteObject[] = [
  {
    path: ':language',
    element: (
      <Suspense fallback={branchFallback}>
        <Outlet />
      </Suspense>
    ),
    children: [
      { index: true, element: <HomeRedirect /> },
      { path: 'token/:id', element: <EmailVerificationView /> },
      { path: 'offline', element: <OfflineView /> },
      { path: 'settings/biometrics', element: <BiometricsView /> },
    ],
  },
]

/**
 * Catch-all for paths without a language prefix (shared links / printed QR codes
 * point at /c/:shortId). Prepend the resolved language and keep the rest of the
 * path. If the URL already has a valid language basename but matched nothing, fall
 * back to that language's home so we never loop on the splat.
 */
const LanguageRedirect = () => {
  const location = useLocation()
  const validBasename = getValidBasename()

  if (validBasename !== '/') {
    return <Navigate to={() => validBasename} replace />
  }

  const basename = languageEnumToBasename(getInitialLanguage())
  const suffix = location.pathname === '/' ? '' : location.pathname
  return <Navigate to={() => `/${basename}${suffix}${location.search}`} replace />
}

const RootLayout = () => {
  const routeLinks = useRoutes()
  return (
    <RoutesProvider routes={routeLinks}>
      <NavigationChange />
      <PointerEventsMember>
        <ScrollRestoration />
        <Outlet />
      </PointerEventsMember>
    </RoutesProvider>
  )
}

export const AppRouter = () => {
  // Router must be created only once - recreating it on every render causes stale
  // navigate references (URL changes but UI doesn't update).
  const router = useMemo(
    () =>
      createBrowserRouter([
        {
          path: '/',
          element: <RootLayout />,
          errorElement: <AppErrorBoundary />,
          children: [
            ...surveyRoutes,
            ...reportRoutes,
            ...disclosureSubmissionRoutes,
            ...homeRoutes,
            // index = bare "/" (splat "*" does not match an empty remainder)
            { index: true, element: <LanguageRedirect /> },
            { path: '*', element: <LanguageRedirect /> },
          ],
        },
        { path: '/*', Component: RouterFullScreenLoader },
      ]),
    []
  )

  const { isAuthenticated } = useAppLock()

  return (
    <>
      {/* App PIN lock: keep the router mounted (preserves input state) but hide it
          behind the landing/lock screen until authenticated. No-op on the web. */}
      {!isAuthenticated && (
        <Suspense fallback={<FullScreenLoader />}>
          <LandingPage />
        </Suspense>
      )}
      <div className={isAuthenticated ? '' : 'absolute top-0 hidden max-h-screen overflow-hidden'}>
        <RouterProvider router={router} />
      </div>
    </>
  )
}
